// stores/tonnageStatsStore.ts
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { TonnageRow } from '../types';
import apiClient from '../../../../../apiClient';
import { getCurrentMonthNumber, getCurrentWeekNumber } from '../lib/dateHelpers';

interface TonnageStatsState {
    yearOne: number;
    yearTwo: number;
    weekOne: number;
    weekTwo: number;
    monthOne: number;
    monthTwo: number;
    selectedUnits: string[];    // [] = all
    selectedAirlines: string[]; // [] = all
    availableYears: number[];
    yearsLoaded: boolean;

    dataByYear: Record<number, TonnageRow[]>;
    loadingYears: Set<number>;
    error: string | null;

    setYearOne: (year: number) => void;
    setYearTwo: (year: number) => void;
    setWeekOne: (week: number) => void;
    setWeekTwo: (week: number) => void;
    setMonthOne: (month: number) => void;
    setMonthTwo: (month: number) => void;
    setSelectedUnits: (units: string[]) => void;
    setSelectedAirlines: (airlines: string[]) => void;
    ensureYearLoaded: (year: number) => Promise<void>;
    fetchAvailableYears: () => Promise<void>;
}

const currentYear = new Date().getFullYear();

export const useTonnageStatsStore = create<TonnageStatsState>()(
    subscribeWithSelector((set, get) => ({
        yearOne: currentYear - 1,
        yearTwo: currentYear,
        weekOne: getCurrentWeekNumber(),
        weekTwo: getCurrentWeekNumber(),
        monthOne: getCurrentMonthNumber(),
        monthTwo: getCurrentMonthNumber(),
        selectedUnits: [],
        selectedAirlines: [],
        availableYears: [],
        yearsLoaded: false,

        dataByYear: {},
        loadingYears: new Set(),
        error: null,

        setYearOne: (year) => set({ yearOne: year }),
        setYearTwo: (year) => set({ yearTwo: year }),
        setWeekOne: (week) => set({ weekOne: week }),
        setWeekTwo: (week) => set({ weekTwo: week }),
        setMonthOne: (month) => set({ monthOne: month }),
        setMonthTwo: (month) => set({ monthTwo: month }),
        setSelectedUnits: (units) => set({ selectedUnits: units }),
        setSelectedAirlines: (airlines) => set({ selectedAirlines: airlines }),

        fetchAvailableYears: async () => {
            if (get().yearsLoaded) return; // avoid refetching on remount
            try {
                const res = await apiClient.get('/corp-analytics/tonnage/years');
                if (res.status !== 200) throw new Error('Failed to load available years');
                const years: number[] = res.data.filter(year => year > 2000);
                set({ availableYears: years, yearsLoaded: true });
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to load available years' });
            }
        },

        ensureYearLoaded: async (year) => {
            const { dataByYear, loadingYears } = get();
            if (dataByYear[year] || loadingYears.has(year)) return;

            set({ loadingYears: new Set(loadingYears).add(year) });
            try {
                const res = await apiClient.get(`/corp-analytics/tonnage?year=${year}`);
                if (res.status !== 200) throw new Error(`Failed to load tonnage data for ${year}`);
                const rows: TonnageRow[] = await res.data;
                set((state) => ({ dataByYear: { ...state.dataByYear, [year]: rows } }));
            } catch (err) {
                set({ error: err instanceof Error ? err.message : 'Failed to load tonnage data' });
            } finally {
                set((state) => {
                    const next = new Set(state.loadingYears);
                    next.delete(year);
                    return { loadingYears: next };
                });
            }
        },
    }))
);

// Root-level reactions: only Year One / Year Two changes trigger a fetch.
// Week/Month/Units/Airlines changes never hit this subscription.
useTonnageStatsStore.subscribe(
    (state) => state.yearOne,
    (year) => useTonnageStatsStore.getState().ensureYearLoaded(year)
);
useTonnageStatsStore.subscribe(
    (state) => state.yearTwo,
    (year) => useTonnageStatsStore.getState().ensureYearLoaded(year)
);